// Document JRAF complet (format v1).

import { z } from 'zod';
import { JRAF_DEFAULT_CANVAS, JRAF_LIMITS as L } from '../limits';
import { jrafObjectSchema } from './objects';
import {
  colorSchema,
  displayTextSchema,
  jrafIdSchema,
  jrafParamIdSchema,
  numExprSchema,
} from './primitives';
import { jrafStepSchema } from './steps';

export const JRAF_FORMAT = 'jraf';
export const CURRENT_FORMAT_VERSION = 1;

// ------------------------------------------------------------- paramètres

export const numberParameterSchema = z
  .object({
    id: jrafParamIdSchema,
    type: z.literal('number'),
    default: z.number().finite().min(-L.maxParameterMagnitude).max(L.maxParameterMagnitude),
    min: z.number().finite().min(-L.maxParameterMagnitude).max(L.maxParameterMagnitude),
    max: z.number().finite().min(-L.maxParameterMagnitude).max(L.maxParameterMagnitude),
    step: z.number().positive().max(L.maxParameterMagnitude).optional(),
    label: displayTextSchema(40).optional(),
  })
  .strict();

/** Grandeur dérivée des paramètres précédents (`260 / a`). */
export const constantParameterSchema = z
  .object({
    id: jrafParamIdSchema,
    type: z.literal('constant'),
    value: numExprSchema,
  })
  .strict();

export const jrafParameterSchema = z.discriminatedUnion('type', [
  numberParameterSchema,
  constantParameterSchema,
]);
export type JrafParameter = z.infer<typeof jrafParameterSchema>;

// -------------------------------------------------------------- ressources

/** Chemin dans le bucket des animations : jamais une URL, jamais de `..`. */
export const ASSET_PATH_RE = /^(?!.*\.\.)(?!.*\/\/)[a-z0-9][a-z0-9/_.-]{0,200}$/;
export const AUDIO_EXTENSIONS = ['m4a', 'mp3', 'webm', 'ogg', 'aac', 'wav'] as const;
export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'] as const;

const wordCueSchema = z
  .object({
    w: z.string().min(1).max(60),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
  })
  .strict();

export const audioAssetSchema = z
  .object({
    id: jrafIdSchema,
    kind: z.literal('audio'),
    path: z.string().regex(ASSET_PATH_RE, 'chemin de ressource invalide'),
    durationMs: z
      .number()
      .int()
      .min(100)
      .max(L.maxSceneTimelineMs * 2),
    /**
     * `recorded` : voix enregistrée (la tienne ou une autre) ; `synthetic` :
     * voix de synthèse serveur, posée à l'export vidéo sur les parties sans
     * voix enregistrée (lot 7) — toujours signalée (générique, description)
     */
    voice: z.enum(['recorded', 'synthetic']).default('recorded'),
    /** horodatage des mots (Whisper), pour caler sous-titres et étapes */
    words: z.array(wordCueSchema).max(L.maxWordCues).optional(),
    /** qui parle (générique : « Voix : … ») — format 2 du lecteur */
    speaker: z.string().trim().min(1).max(60).optional(),
    /**
     * compte qui a ENREGISTRÉ la voix (son avatar animé s'affiche pendant
     * qu'elle joue, migration 0675) ; jamais sur une voix de synthèse
     */
    speakerId: z.string().uuid().optional(),
  })
  .strict();

export const imageAssetSchema = z
  .object({
    id: jrafIdSchema,
    kind: z.literal('image'),
    path: z.string().regex(ASSET_PATH_RE, 'chemin de ressource invalide'),
    width: z.number().int().min(1).max(8000).optional(),
    height: z.number().int().min(1).max(8000).optional(),
  })
  .strict();

export const jrafAssetSchema = z.discriminatedUnion('kind', [audioAssetSchema, imageAssetSchema]);
export type JrafAsset = z.infer<typeof jrafAssetSchema>;

// ------------------------------------------------------------------ scènes

export const HORA_KEYS = ['H', 'O', 'R', 'A'] as const;
export type HoraKey = (typeof HORA_KEYS)[number];

// ------------------------------------------------------------- distribution

/** Rôles qui parlent : Speaker 1 (narrateur par défaut), 2, 3. */
export const CAST_ROLE_IDS = ['s1', 's2', 's3'] as const;
export type CastRoleId = (typeof CAST_ROLE_IDS)[number];

/**
 * Un rôle de la distribution (« Le prof », « L'élève », « Léa ») : monologue
 * = 1 rôle (dit par une ou plusieurs personnes) ; dialogue = 2 ou 3 rôles.
 * Chaque voix est enregistrée par qui tient ce rôle (Speaker 1, 2, 3).
 */
export const castRoleSchema = z
  .object({
    id: z.enum(CAST_ROLE_IDS),
    label: z
      .string()
      .trim()
      .min(1)
      .max(40)
      .regex(/^[^$\\{}]+$/, 'un nom simple, sans formule'),
  })
  .strict();
export type JrafCastRole = z.infer<typeof castRoleSchema>;

export const narrationSchema = z
  .object({
    /** rôle qui dit cette narration (dialogue) ; absent : Speaker 1 */
    speaker: z.enum(CAST_ROLE_IDS).optional(),
    /** sous-titres (affichés) ; `$…$` permis */
    text: displayTextSchema(L.maxNarrationChars),
    /** ce que dit la voix (sans LaTeX : « a au carré ») ; guide de lecture pour l'enregistrement */
    say: z.string().trim().min(1).max(L.maxNarrationChars).optional(),
    /** voix enregistrée ; absente → voix du navigateur dans l'app */
    audioAssetId: jrafIdSchema.nullable().optional(),
  })
  .strict();
export type JrafNarration = z.infer<typeof narrationSchema>;

export const sceneSchema = z
  .object({
    id: jrafIdSchema,
    title: displayTextSchema(80),
    hora: z.enum(HORA_KEYS).optional(),
    /**
     * scène « À toi » : partie interactive, payante. Toujours APRÈS les scènes
     * gratuites ; sans accès, le serveur la retire (`stripInteractive`).
     */
    interactive: z.boolean().default(false),
    narration: narrationSchema,
    steps: z.array(jrafStepSchema).max(L.maxStepsPerScene).default([]),
    advance: z
      .object({ mode: z.enum(['auto', 'manual']).default('auto') })
      .strict()
      .default({}),
    transition: z.enum(['fade', 'cut']).default('fade'),
  })
  .strict();
export type JrafScene = z.infer<typeof sceneSchema>;

// --------------------------------------------------------------- document

export const metadataSchema = z
  .object({
    subject: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/, 'matière : slug en minuscules'),
    levels: z
      .array(z.string().regex(/^[a-z0-9]{1,10}$/, 'niveau scolaire : slug court'))
      .max(9)
      .default([]),
    language: z.enum(['fr-FR']).default('fr-FR'),
    /** SKILLCARD source (création par l'IA, accès à la partie interactive) */
    cardId: z.string().uuid().optional(),
    summary: displayTextSchema(300).optional(),
  })
  .strict();

export const canvasSchema = z
  .object({
    width: z.number().int().min(160).max(1920).default(JRAF_DEFAULT_CANVAS.width),
    height: z.number().int().min(160).max(1920).default(JRAF_DEFAULT_CANVAS.height),
    background: colorSchema.default('night'),
  })
  .strict();

/**
 * Accroche de la VIDÉO publiée (Shorts, TikTok), juste avant « JeRaisonne
 * présente » : la question que tout élève se pose sur le sujet, drôle ou
 * intrigante (« C'est quoi, Pythagore ??? », « Stœchiométrie ? À tes
 * souhaits ! »). Écrite par l'IA ; dessinée par le lecteur (mots qui
 * claquent). Pas dans l'app : l'élève y est déjà.
 */
export const hookSchema = z
  .object({
    /** texte affiché, sans formule */
    text: z
      .string()
      .trim()
      .min(3)
      .max(L.maxHookChars)
      .regex(/^[^$\\{}]+$/, 'une phrase simple, sans formule ($, \\, accolades)'),
    /** mot ou groupe de mots du texte à faire ressortir (en or) */
    emphasis: z.string().trim().min(1).max(L.maxHookChars).optional(),
    /** ce que dit la voix, si la prononciation diffère du texte */
    say: z
      .string()
      .trim()
      .min(1)
      .max(L.maxHookChars * 2)
      .optional(),
  })
  .strict();
export type JrafHook = z.infer<typeof hookSchema>;

/**
 * Intro (« JeRaisonne présente ») et générique : ajoutés par le LECTEUR
 * (`withBookends`), jamais écrits par l'IA. Le document porte l'accroche de
 * la vidéo (lecteur 3) et les voix enregistrées de ces scènes (lecteur 2).
 */
export const bookendsSchema = z
  .object({
    hook: hookSchema.optional(),
    hookAudioAssetId: jrafIdSchema.nullable().optional(),
    introAudioAssetId: jrafIdSchema.nullable().optional(),
    /** générique de la vidéo publiée (« Défie tes potes ! … ») */
    outroAudioAssetId: jrafIdSchema.nullable().optional(),
  })
  .strict();

export const playerSchema = z
  .object({
    allowPrevious: z.boolean().default(true),
    allowNext: z.boolean().default(true),
    allowReplay: z.boolean().default(true),
    showProgress: z.boolean().default(true),
    captions: z.boolean().default(true),
  })
  .strict();

export const jrafDocumentSchema = z
  .object({
    format: z.literal(JRAF_FORMAT),
    formatVersion: z.literal(CURRENT_FORMAT_VERSION),
    id: jrafIdSchema,
    title: displayTextSchema(L.maxTitleChars),
    metadata: metadataSchema,
    canvas: canvasSchema.default({}),
    parameters: z.array(jrafParameterSchema).max(L.maxParameters).default([]),
    assets: z.array(jrafAssetSchema).max(L.maxAssets).default([]),
    objects: z.array(jrafObjectSchema).max(L.maxObjects).default([]),
    scenes: z.array(sceneSchema).min(1).max(L.maxScenes),
    player: playerSchema.default({}),
    bookends: bookendsSchema.optional(),
    /** distribution (monologue : 1 rôle ; dialogue : 2 ou 3) ; absente : un narrateur */
    cast: z.array(castRoleSchema).min(1).max(CAST_ROLE_IDS.length).optional(),
  })
  .strict();

/** Document validé (valeurs par défaut appliquées). */
export type JrafDocument = z.infer<typeof jrafDocumentSchema>;
/** Document tel qu'on l'écrit (champs par défaut facultatifs). */
export type JrafDocumentInput = z.input<typeof jrafDocumentSchema>;
